class ImageTabs extends HTMLElement {
  static get observedAttributes() {
    return ["selected-index"];
  }

  constructor() {
    super();

    if (!this.shadowRoot) {
      const tpl = this.querySelector("template");
      if (tpl) {
        this.attachShadow({ mode: "open" }).appendChild(tpl.content.cloneNode(true));
      } else {
        this.attachShadow({ mode: "open" });
      }
    }

    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._mo = null;

    this.enableFade = true;
    this.fadeMs = 200;

    // content height animation
    this.enableContentHeightAnim = true;
    this.contentHeightMs = 260;

    this._tContent = null;
  }

  connectedCallback() {
    this.addEventListener("click", this._onClick);
    this.addEventListener("keydown", this._onKeyDown);

    this._mo = new MutationObserver(() => this._refresh(true));
    this._mo.observe(this, { childList: true, subtree: true });

    this._refresh(true);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("keydown", this._onKeyDown);
    if (this._mo) this._mo.disconnect();
    if (this._tContent) window.clearTimeout(this._tContent);
  }

  get selectedIndex() {
    const n = Number.parseInt(this.getAttribute("selected-index") || "0", 10);
    return Number.isFinite(n) ? n : 0;
  }

  set selectedIndex(index) {
    const next = this._clampIndex(index);
    this.setAttribute("selected-index", String(next));
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== "selected-index") return;

    const oldIndex = oldValue === null ? null : Number.parseInt(oldValue, 10);
    const next = this._clampIndex(Number.parseInt(newValue, 10));

    if (String(next) !== String(newValue)) {
      this.setAttribute("selected-index", String(next));
      return;
    }

    this._updateCssVars();
    this._updateButtonsA11y(next);
    this._applyState(oldIndex, next);
  }

  get buttons() {
    return Array.from(this.querySelectorAll('[slot="title"]'));
  }

  get imagePanels() {
    return Array.from(this.querySelectorAll('[slot="image"]'));
  }

  get contentPanels() {
    return Array.from(this.querySelectorAll('[slot="content"]'));
  }

  get _contentWrap() {
    return this.shadowRoot?.querySelector('[part="content-wrap"]') || null;
  }

  _clampIndex(i) {
    const max = this.buttons.length - 1;
    if (max < 0) return 0;
    const n = Number.isFinite(i) ? i : 0;
    return Math.min(Math.max(n, 0), max);
  }

  _refresh(applyState) {
    const next = this._clampIndex(this.selectedIndex);
    if (next !== this.selectedIndex) {
      this.setAttribute("selected-index", String(next));
      return;
    }

    this._updateCssVars();
    this._setupAccessibility();

    if (applyState) this._applyState(null, next);
  }

  _updateCssVars() {
    this.style.setProperty("--selected-index", String(this.selectedIndex));
    this.style.setProperty("--item-count", String(this.buttons.length));
  }

  _onClick(e) {
    const btn = e.target.closest('[slot="title"]');
    if (!btn) return;
    const idx = this.buttons.indexOf(btn);
    if (idx >= 0) this.selectedIndex = idx;
  }

  _onKeyDown(e) {
    const btn = e.target.closest('[slot="title"]');
    if (!btn) return;

    const i = this.buttons.indexOf(btn);
    if (i < 0) return;

    let next = i;

    switch (e.key) {
      case "ArrowLeft":
      case "Left":
        next = i - 1;
        break;
      case "ArrowRight":
      case "Right":
        next = i + 1;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = this.buttons.length - 1;
        break;
      case "Enter":
      case " ":
        next = i;
        break;
      default:
        return;
    }

    e.preventDefault();
    next = this._clampIndex(next);
    this.selectedIndex = next;
    this.buttons[next]?.focus();
  }

  _setupAccessibility() {
    const uid = this.getAttribute("data-tabs-uid") || this._uid();
    this.setAttribute("data-tabs-uid", uid);

    this.buttons.forEach((btn, i) => {
      btn.setAttribute("role", "tab");
      btn.setAttribute("aria-selected", i === this.selectedIndex ? "true" : "false");
      btn.tabIndex = i === this.selectedIndex ? 0 : -1;

      const content = this.contentPanels[i];
      if (content) {
        content.setAttribute("role", "tabpanel");
        const id = content.id || `image-tabs-panel-${uid}-${i}`;
        content.id = id;
        btn.setAttribute("aria-controls", id);
      }
    });
  }

  _updateButtonsA11y(activeIndex) {
    this.buttons.forEach((btn, i) => {
      btn.setAttribute("aria-selected", i === activeIndex ? "true" : "false");
      btn.tabIndex = i === activeIndex ? 0 : -1;
    });
  }

  _applyState(oldIndex, newIndex) {
    this._switchImages(newIndex);
    this._switchContent(oldIndex, newIndex);
  }

  _switchImages(newIndex) {
    const list = this.imagePanels;

    list.forEach((panel, i) => {
      const isActive = i === newIndex;

      if (!this.enableFade) {
        panel.hidden = !isActive;
        panel.style.opacity = isActive ? "1" : "0";
        return;
      }

      if (isActive) {
        panel.hidden = false;
        panel.style.transition = `opacity ${this.fadeMs}ms ease`;
        requestAnimationFrame(() => (panel.style.opacity = "1"));
      } else {
        panel.style.transition = `opacity ${this.fadeMs}ms ease`;
        panel.style.opacity = "0";
        window.setTimeout(() => {
          if (i !== this.selectedIndex) panel.hidden = true;
        }, this.fadeMs);
      }
    });
  }

  _switchContent(oldIndex, newIndex) {
    const wrap = this._contentWrap;
    const panels = this.contentPanels;
    if (!wrap || panels.length === 0) return;

    if (this._tContent) window.clearTimeout(this._tContent);

    const next = panels[newIndex];
    const prev = oldIndex === null ? null : panels[oldIndex];

    // First paint: show only active
    if (oldIndex === null || !prev) {
      panels.forEach((p, i) => {
        p.hidden = i !== newIndex;
        p.style.opacity = i === newIndex ? "1" : "0";
        p.style.transition = "";
        p.style.position = "";
        p.style.inset = "";
      });
      wrap.style.height = "auto";
      wrap.style.transition = "";
      return;
    }

    // Ensure prev is visible in flow for measuring
    prev.hidden = false;
    prev.style.position = "";
    prev.style.inset = "";
    prev.style.opacity = "1";

    const prevH = prev.getBoundingClientRect().height || prev.scrollHeight || 0;

    // Make next measurable without affecting layout:
    // unhide + absolute + hidden visibility
    next.hidden = false;
    next.style.position = "absolute";
    next.style.inset = "0 0 auto 0";
    next.style.width = "100%";
    next.style.visibility = "hidden";
    next.style.opacity = "0";

    // measure next height
    const nextH = next.getBoundingClientRect().height || next.scrollHeight || 0;

    // now prepare animation: keep next absolute but visible, fade in
    next.style.visibility = "visible";

    // lock wrapper height at prev height before anim
    wrap.style.transition = "";
    wrap.style.height = `${prevH}px`;

    // set transitions
    prev.style.transition = `opacity ${this.fadeMs}ms ease`;
    next.style.transition = `opacity ${this.fadeMs}ms ease`;

    // next frame: animate height + opacity
    requestAnimationFrame(() => {
      if (this.enableContentHeightAnim) {
        wrap.style.transition = `height ${this.contentHeightMs}ms ease`;
        wrap.style.height = `${nextH}px`;
      } else {
        wrap.style.height = `${nextH}px`;
      }

      next.style.opacity = "1";
      prev.style.opacity = "0";
    });

    const doneMs = Math.max(this.fadeMs, this.enableContentHeightAnim ? this.contentHeightMs : 0) + 30;

    this._tContent = window.setTimeout(() => {
      // finalize: hide prev
      prev.hidden = true;
      prev.style.opacity = "";
      prev.style.transition = "";

      // put next back into normal flow
      next.style.position = "";
      next.style.inset = "";
      next.style.width = "";
      next.style.opacity = "";
      next.style.transition = "";
      next.style.visibility = "";

      // hide all others
      panels.forEach((p, i) => {
        if (i !== newIndex) p.hidden = true;
      });

      // restore wrap natural height
      wrap.style.transition = "";
      wrap.style.height = "auto";
    }, doneMs);
  }

  _uid() {
    return Math.random().toString(16).slice(2);
  }
}

customElements.define("image-tabs", ImageTabs);
